/**
 * Heatmap — the day-of-week × hour congestion grid as a semantic HTML table.
 *
 * A 7×24 grid is natively tabular, so this renders a real `<table>` with day row
 * headers and hour column headers: screen readers can navigate it cell-by-cell
 * with full context, and the colour is a single-hue *luminance* ramp (light →
 * dark, set via the `--i` 0..1 custom property in CSS), which reads correctly in
 * greyscale and on the red/green axis. Each cell carries a self-contained
 * `aria-label` with the actual numbers, so meaning never rests on colour alone
 * (WCAG 2.1 AA, see DECISIONS.md). visx stays the choice for line/area charts; a
 * grid like this is better served by a table.
 *
 * Keyboard: the grid is a single tab stop (roving tabindex, the WAI-ARIA grid
 * pattern) — Tab lands on one cell, the arrow keys / Home / End move the focus
 * between cells, and Enter/Space opens the drill for the focused populated cell.
 * This closes the a11y gap without forcing a keyboard user through 168 tab stops.
 *
 * Detail on demand comes from ONE shared, `position: fixed` popover (never 168
 * native tooltips): on a fine pointer it follows the hovered cell; on a coarse
 * pointer a tap toggles it; for keyboard it opens on Enter/Space (role=status
 * announces the reading). Fixed positioning keeps it clear of the scroll
 * container's overflow clipping. Any scroll or resize moves the anchor cell out
 * from under the fixed popover, so both dismiss it — it never goes stale.
 *
 * The reading is a drill into what the payload already carries: the slot average,
 * its share of the hospital's weekly peak, whether it runs busier/quieter than
 * that hospital's typical slot, and how confident the average is (sample count).
 * No fabricated distribution or today-vs-slot comparison (deferred to #218).
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import {
  cellRank,
  formatHourRange,
  intensity,
  sampleConfidence,
  type HeatmapGridRow,
} from "@/lib/heatmap";

interface HeatmapProps {
  rows: HeatmapGridRow[];
  /** Top of the colour scale (largest average waiting across the grid). */
  max: number;
  /** This hospital's mean slot average — the drill's "typical" baseline. */
  mean: number;
}

/** Italian confidence labels for the drill reading. */
const CONFIDENCE_LABEL = {
  low: "affidabilità bassa",
  medium: "affidabilità media",
  high: "affidabilità alta",
} as const;

/** Italian standing-vs-typical labels for the drill reading. */
const VS_TYPICAL_LABEL = {
  busier: "più affollato del solito qui",
  quieter: "più tranquillo del solito qui",
  typical: "in linea con il solito qui",
} as const;

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const LAST_HOUR = 23;

function cellLabel(dayLong: string, hour: number, cell: HeatmapGridRow["cells"][number]): string {
  const slot = `${dayLong} ore ${formatHourRange(hour)}`;
  if (cell === null) return `${slot}: nessun dato`;
  const avg = Math.round(cell.avgWaiting);
  const obs = cell.sampleCount === 1 ? "1 rilevazione" : `${cell.sampleCount} rilevazioni`;
  return `${slot}: media ${avg} in attesa (${obs})`;
}

/** The content the popover shows for one cell, derived from its dataset. */
interface PopData {
  day: string;
  hour: string;
  /** Rounded average, for display. */
  avg: number;
  /** Raw (unrounded) average, for honest ranking vs. the raw peak/mean. */
  avgRaw: number;
  samples: number;
  i: number;
}

function popDataFrom(el: HTMLElement): PopData {
  return {
    day: el.dataset.day ?? "",
    hour: el.dataset.hour ?? "",
    avg: Number(el.dataset.avg),
    avgRaw: Number(el.dataset.avgRaw),
    samples: Number(el.dataset.samples),
    i: Number(el.dataset.i),
  };
}

export function Heatmap({ rows, max, mean }: HeatmapProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // The active cell is a DOM node (the popover is positioned relative to its
  // rect and driven by delegated listeners, so we track the element itself).
  const [active, setActive] = useState<HTMLElement | null>(null);
  const pop = active ? popDataFrom(active) : null;

  // Roving tabindex: exactly one cell is in the tab order at a time. Defaults to
  // the first populated cell (so Tab lands on something meaningful), then follows
  // the arrow keys / a direct click.
  // (React Compiler memoizes this; a manual useMemo can't be preserved through
  // the early return inside the scan.)
  const firstPopulated = ((): { r: number; c: number } => {
    for (let r = 0; r < rows.length; r++) {
      for (let c = 0; c < HOURS.length; c++) if (rows[r]?.cells[c]) return { r, c };
    }
    return { r: 0, c: 0 };
  })();
  const [focusRC, setFocusRC] = useState<{ r: number; c: number } | null>(null);
  const tab = focusRC ?? firstPopulated;

  const cellAt = (r: number, c: number): HTMLElement | null =>
    scrollRef.current?.querySelector<HTMLElement>(`[data-r="${r}"][data-c="${c}"]`) ?? null;

  const moveFocus = (r: number, c: number) => {
    const nr = Math.max(0, Math.min(rows.length - 1, r));
    const nc = Math.max(0, Math.min(LAST_HOUR, c));
    const el = cellAt(nr, nc);
    if (el) {
      el.focus();
      setFocusRC({ r: nr, c: nc });
    }
  };

  // All keyboard interaction lives here (fresh closures over rows/state) — arrow
  // roving + Home/End, and Enter/Space to open the drill on the focused cell.
  const onGridKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>("[data-r]");
    if (!target) return;
    const r = Number(target.dataset.r);
    const c = Number(target.dataset.c);
    switch (e.key) {
      case "Enter":
      case " ":
      case "Spacebar":
        if (target.matches("[data-heat-cell]")) {
          e.preventDefault();
          setActive((prev) => (prev === target ? null : target));
        }
        return;
      case "ArrowLeft":
        e.preventDefault();
        moveFocus(r, c - 1);
        return;
      case "ArrowRight":
        e.preventDefault();
        moveFocus(r, c + 1);
        return;
      case "ArrowUp":
        e.preventDefault();
        moveFocus(r - 1, c);
        return;
      case "ArrowDown":
        e.preventDefault();
        moveFocus(r + 1, c);
        return;
      case "Home":
        e.preventDefault();
        moveFocus(r, 0);
        return;
      case "End":
        e.preventDefault();
        moveFocus(r, LAST_HOUR);
        return;
    }
  };

  // Outline the active cell (the sole pulse accent in the heatmap) imperatively,
  // since the active cell is tracked as a DOM node rather than via render state.
  useEffect(() => {
    if (!active) return;
    // `active` is a DOM node tracked in state; toggling its data-attribute is a
    // deliberate imperative outline (avoids re-rendering every cell on hover), not
    // a React-state mutation. Cleaned up on change/unmount.
    // eslint-disable-next-line react-hooks/immutability -- imperative DOM accent on a tracked node
    active.dataset.active = "1";
    return () => {
      delete active.dataset.active;
    };
  }, [active]);

  // Position the popover: centred over the cell and placed above; flip below if
  // it would clip the top, then clamp within 8px of every viewport edge. No
  // caret. Runs before paint so it never flashes at a stale spot.
  useLayoutEffect(() => {
    const el = popRef.current;
    if (!active || !el) return;
    const cell = active.getBoundingClientRect();
    const box = el.getBoundingClientRect();
    const margin = 8;
    let left = cell.left + cell.width / 2 - box.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - box.width - margin));
    let top = cell.top - box.height - margin;
    if (top < margin) top = cell.bottom + margin;
    top = Math.max(margin, Math.min(top, window.innerHeight - box.height - margin));
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, [active, pop?.day, pop?.hour]);

  // Delegated pointer interaction on the scroll container: hover on a fine
  // pointer, tap to toggle on a coarse one. Dismissal (scroll/resize/Escape/
  // outside-tap) is here too; keyboard is handled by onGridKeyDown in the JSX.
  useEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const fine = window.matchMedia("(pointer: fine)").matches;
    const coarse = window.matchMedia("(pointer: coarse)").matches;

    function cellFrom(target: EventTarget | null): HTMLElement | null {
      if (!(target instanceof Element)) return null;
      const el = target.closest<HTMLElement>("[data-heat-cell]");
      return el ?? null;
    }

    const cleanups: Array<() => void> = [];
    const on = (
      node: HTMLElement | Document | Window,
      type: string,
      handler: EventListener,
      opts?: AddEventListenerOptions | boolean,
    ) => {
      node.addEventListener(type, handler, opts);
      cleanups.push(() => node.removeEventListener(type, handler, opts));
    };

    if (fine) {
      // Only react when actually over a cell, so crossing the inter-cell gaps
      // doesn't flicker the popover shut; mouseleave clears on exit.
      on(scroll, "mouseover", (e) => {
        const cell = cellFrom(e.target);
        if (cell) setActive((prev) => (prev === cell ? prev : cell));
      });
      on(scroll, "mouseleave", () => setActive(null));
    }

    if (coarse) {
      on(scroll, "click", (e) => {
        const cell = cellFrom(e.target);
        if (!cell) {
          setActive(null);
          return;
        }
        setActive((prev) => (prev === cell ? null : cell));
      });
      // A tap outside the grid dismisses (inside taps are handled above and are
      // still contained, so this only fires for genuine outside taps).
      on(document, "click", (e) => {
        if (!scroll.contains(e.target as Node)) setActive(null);
      });
    }

    // Focusing a cell (Tab in, arrow move, or a click that focuses) syncs the
    // roving position and clears a popover that belongs to a different cell.
    on(scroll, "focusin", (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>("[data-r]");
      if (el) setFocusRC({ r: Number(el.dataset.r), c: Number(el.dataset.c) });
      const cell = cellFrom(e.target);
      setActive((prev) => (prev && prev !== cell ? null : prev));
    });
    on(scroll, "focusout", (e) => {
      const next = (e as FocusEvent).relatedTarget as Node | null;
      if (!next || !scroll.contains(next)) setActive(null);
    });

    // Any scroll or resize moves the anchor cell out from under the fixed
    // popover, so all dismiss it — a pinned-but-detached popover would be worse
    // than none (spec: never stale). Listen on both the page (window, capture —
    // scroll doesn't bubble) and the grid's own horizontal scroll container, so
    // either kind of scroll dismisses.
    on(window, "scroll", () => setActive(null), true);
    on(scroll, "scroll", () => setActive(null));
    on(window, "resize", () => setActive(null));
    on(document, "keydown", (e) => {
      if ((e as KeyboardEvent).key === "Escape") setActive(null);
    });

    return () => cleanups.forEach((c) => c());
  }, []);

  return (
    <>
      <div
        className="heatmap__scroll"
        role="region"
        aria-label="Tabella affollamento per giorno e ora"
        ref={scrollRef}
        onKeyDown={onGridKeyDown}
      >
        <table className="heatmap">
          <thead>
            <tr>
              <th scope="col" className="heatmap__corner">
                <span className="sr-only">Giorno</span>
              </th>
              {HOURS.map((h) => (
                <th key={h} scope="col" className="heatmap__hour">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={row.dayOfWeek}>
                <th scope="row" className="heatmap__day" title={row.dayLong}>
                  {row.dayShort}
                </th>
                {row.cells.map((cell, h) => {
                  const label = cellLabel(row.dayLong, h, cell);
                  // Roving tabindex: only the one "tab" cell is in the tab order;
                  // every cell is programmatically focusable (-1) so the arrow
                  // keys can move to it (empty cells included, for uniform nav).
                  const tabIndex = tab.r === r && tab.c === h ? 0 : -1;
                  if (cell === null) {
                    return (
                      <td
                        key={h}
                        className="heat-cell heat-cell--empty"
                        aria-label={label}
                        tabIndex={tabIndex}
                        data-r={r}
                        data-c={h}
                      />
                    );
                  }
                  const i = intensity(cell.avgWaiting, max);
                  return (
                    <td
                      key={h}
                      className="heat-cell"
                      tabIndex={tabIndex}
                      style={{ "--i": i } as CSSProperties}
                      aria-label={label}
                      data-heat-cell=""
                      data-r={r}
                      data-c={h}
                      data-day={row.dayLong}
                      data-hour={formatHourRange(h)}
                      data-avg={Math.round(cell.avgWaiting)}
                      data-avg-raw={cell.avgWaiting}
                      data-samples={cell.sampleCount}
                      data-i={i}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="heatmap__hint">Tocca una fascia per i dettagli.</p>

      <div className="heat-pop" role="status" ref={popRef} data-show={pop ? "1" : "0"}>
        {pop && (
          <>
            <div className="heat-pop__slot">
              {pop.day} · {pop.hour}
            </div>
            <div className="heat-pop__stat">
              <span
                className="heat-pop__swatch"
                aria-hidden
                style={{ "--i": pop.i } as CSSProperties}
              />
              <span className="heat-pop__value">~{pop.avg}</span>
              <span className="heat-pop__unit">in attesa</span>
            </div>
            {/* Drill reading — how this slot ranks within the hospital's own week,
                honest to the payload we have: fraction of the weekly peak, a
                busier/quieter-than-typical read, and how confident that average is
                given the sample count. Ranked on the RAW average vs the raw peak
                so the busiest slot reads a true 100%. No fabricated distribution
                (see #218). */}
            {(() => {
              const rank = cellRank(pop.avgRaw, max, mean);
              return (
                <>
                  <div className="heat-pop__drill">
                    {Math.round(rank.fractionOfPeak * 100)}% del picco settimanale ·{" "}
                    {VS_TYPICAL_LABEL[rank.vsTypical]}
                  </div>
                  <div className="heat-pop__samples">
                    media su {pop.samples} {pop.samples === 1 ? "rilevazione" : "rilevazioni"} ·{" "}
                    {CONFIDENCE_LABEL[sampleConfidence(pop.samples)]}
                  </div>
                </>
              );
            })()}
          </>
        )}
      </div>
    </>
  );
}
